import React, { FC, useEffect, useState } from 'react';
import { Intl } from '@erecruit/core';
import { getResourceApi } from '@erecruit/modules';
import {
  Currency,
  FinanceIcon,
  FormItem,
  FormItemColumns,
  Spinner,
  Text,
} from '@erecruit/ui-kit';
import { Resources, Requests } from '@erecruit/types';
import { calculateEstimatedSpend, getHours } from '../helpers';
import BillingRateDescription from './BillingRateDescription';
import { currencySymbol, currencySuffix } from '../helpers';
import { BillingFormItems, BillingFormProps } from '../types';

const { FormattedMessage, injectIntl } = Intl;

export const BillingForm: FC<BillingFormProps> = props => {
  const {
    intl,
    isEdit,
    durationInDays,
    numberOfPositions,
    initialTargetBillRate,
    billingRateParams,
    initialBillingRateData,
  } = props;
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [billingRateData, setBillingRateData] = useState<
    Resources.PositionTemplateBillRates | undefined
  >(initialBillingRateData);
  const { setFieldsValue } = props.form;

  const hours = getHours(props.startTime, props.endTime);
  useEffect(() => {
    const fetchBillingRateData = async () => {
      if (!billingRateParams) {
        return null;
      }

      setError(undefined);
      setLoading(true);

      try {
        const responses = await getResourceApi().getResources([
          new Requests.PositionTemplateBillRatesRequest(billingRateParams),
        ]);
        const data = responses[0]
          .data[0] as Resources.PositionTemplateBillRates;
        setBillingRateData(data);
      } catch (err) {
        setError(intl.formatMessage({ id: messageIds.vms.errors.generic }));
      }

      setLoading(false);
    };

    if (
      !billingRateData &&
      !initialBillingRateData &&
      billingRateParams &&
      !isEdit
    ) {
      fetchBillingRateData();
    }
  }, [
    billingRateData,
    initialBillingRateData,
    billingRateParams,
    setFieldsValue,
    intl,
    isEdit,
  ]);

  const onBillRateChange = (value?: number) => {
    if (!value) {
      return null;
    }

    setFieldsValue({
      estimatedSpend: calculateEstimatedSpend(
        value,
        numberOfPositions,
        hours,
        durationInDays,
      ),
    });
  };

  if (error) {
    return <p>{error}</p>;
  }

  if (loading || !billingRateData) {
    return <Spinner />;
  }

  if (
    !isEdit &&
    !billingRateData.targetBillRate &&
    !billingRateData.maximumBillRate &&
    !billingRateData.minimumBillRate
  ) {
    return (
      <Text>
        <FormattedMessage
          id={messageIds.vms.newPositionWizard.billing.noBillRatesMessage}
        />
      </Text>
    );
  }

  const targetBillRateFormItem = (
    <FormItem
      id={nameof<BillingFormItems>(x => x.targetBillRate)}
      label={intl.formatMessage({
        id: messageIds.vms.newPositionWizard.billing.targetBillRate,
      })}
      hasFeedback
      extra={<BillingRateDescription rateData={billingRateData} />}
      options={{
        initialValue: initialTargetBillRate || billingRateData.targetBillRate,
        rules: [
          {
            type: 'number',
            required: true,
            message: intl.formatMessage({
              id: messageIds.vms.errors.form.requiredField,
            }),
          },
        ],
      }}
    >
      <Currency
        currencySymbol={currencySymbol(intl)}
        icon={FinanceIcon}
        min={billingRateData.minimumBillRate || 0}
        max={billingRateData.maximumBillRate || Infinity}
        suffix={currencySuffix(intl)}
        precision={2}
        onChange={onBillRateChange}
      />
    </FormItem>
  );

  const estimatedSpendInitialValue = calculateEstimatedSpend(
    initialTargetBillRate || billingRateData.targetBillRate || 0,
    numberOfPositions,
    hours,
    durationInDays,
  );

  const estimatedSpendFormItem = (
    <FormItem
      id={nameof<BillingFormItems>(x => x.estimatedSpend)}
      label={intl.formatMessage({
        id: messageIds.vms.newPositionWizard.billing.estimatedSpend,
      })}
      options={{
        initialValue: estimatedSpendInitialValue,
      }}
    >
      <Currency
        currencySymbol={currencySymbol(intl)}
        icon={FinanceIcon}
        precision={2}
        disabled
      />
    </FormItem>
  );

  return (
    <>
      {isEdit ? (
        targetBillRateFormItem
      ) : (
        <FormItemColumns spaceBetween={16}>
          {targetBillRateFormItem}
          {estimatedSpendFormItem}
        </FormItemColumns>
      )}
    </>
  );
};

export default injectIntl(BillingForm);
